/**
 * Maintenance Mode System
 * Handles popup behavior, triggers, cart lockdown, and countdown timers
 */

class MaintenanceMode {
  constructor() {
    this.state = this.getMaintenanceState();
    this.popup = document.querySelector('[data-popup]');
    this.countdowns = document.querySelectorAll('[data-countdown]');
    this.focusableElements = [];
    this.lastFocusedElement = null;
    
    this.init();
  }
  
  /**
   * Get maintenance state from DOM
   */
  getMaintenanceState() {
    const stateElement = document.getElementById('maintenance-mode-state');
    if (!stateElement) return { hideCart: false, showPopup: false, hideAllPages: false };
    
    try {
      return JSON.parse(stateElement.dataset.state || '{}');
    } catch (e) {
      console.error('Error parsing maintenance state:', e);
      return { hideCart: false, showPopup: false, hideAllPages: false };
    }
  }
  
  /**
   * Initialize maintenance mode
   */
  init() {
    // Initialize cart lockdown
    if (this.state.hideCart) {
      this.initCartLockdown();
    }
    
    // Initialize popup
    if (this.state.showPopup && this.popup) {
      this.initPopup();
    }
    
    // Initialize countdowns
    if (this.countdowns.length > 0) {
      this.initCountdowns();
    }
    
    // Fire analytics event if any maintenance mode is active
    if (this.state.hideCart || this.state.showPopup || this.state.hideAllPages) {
      this.fireAnalyticsEvent('maintenance_mode_active', {
        hideCart: this.state.hideCart,
        showPopup: this.state.showPopup,
        hideAllPages: this.state.hideAllPages
      });
    }
  }
  
  /**
   * Initialize cart lockdown - block all add to cart actions
   */
  initCartLockdown() {
    // Intercept all cart add requests
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = args[0];
      
      // Block cart add requests
      if (typeof url === 'string' && url.includes('/cart/add')) {
        console.warn('Cart add blocked: Maintenance mode active');
        this.showCartBlockedNotice();
        throw new Error('Cart functionality is temporarily unavailable');
      }
      
      return originalFetch.apply(this, args);
    };
    
    // Block form submissions to cart
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.action && form.action.includes('/cart/add')) {
        e.preventDefault();
        e.stopPropagation();
        this.showCartBlockedNotice();
        return false;
      }
    }, true);
    
    // Block programmatic button clicks
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-add-to-cart], .add-to-cart, [name="add"]');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        this.showCartBlockedNotice();
        return false;
      }
    }, true);
  }
  
  /**
   * Show notice that cart is blocked
   */
  showCartBlockedNotice() {
    // Fire analytics event
    this.fireAnalyticsEvent('cart_blocked_by_maintenance');
    
    // Show subtle notification
    const notice = document.createElement('div');
    notice.className = 'maintenance-cart-notice';
    notice.textContent = 'Shopping is temporarily unavailable';
    notice.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      font-size: 0.9375rem;
      z-index: 9998;
      animation: slideInUp 0.3s ease;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    `;
    
    document.body.appendChild(notice);
    
    setTimeout(() => {
      notice.style.animation = 'slideOutDown 0.3s ease';
      setTimeout(() => notice.remove(), 300);
    }, 3000);
  }
  
  /**
   * Initialize popup behavior
   */
  initPopup() {
    if (!this.popup) return;
    
    const trigger = this.popup.dataset.trigger;
    const triggerDelay = parseInt(this.popup.dataset.triggerDelay || '0', 10);
    const dismissBehavior = this.popup.dataset.dismiss;
    const frequency = this.popup.dataset.frequency;
    
    // Check if popup should be shown based on frequency capping
    if (this.shouldShowPopup(frequency)) {
      // Show popup based on trigger
      switch (trigger) {
        case 'load':
          setTimeout(() => this.showPopup(), triggerDelay * 1000);
          break;
        case 'delay':
          setTimeout(() => this.showPopup(), triggerDelay * 1000);
          break;
        case 'exit':
          this.initExitIntent();
          break;
        default:
          setTimeout(() => this.showPopup(), triggerDelay * 1000);
      }
    }
    
    // Setup close handlers if dismissible
    if (dismissBehavior !== 'none') {
      this.initPopupCloseHandlers();
    }
    
    // Set backdrop opacity
    const backdropOpacity = parseInt(this.popup.dataset.backdropOpacity || '80', 10) / 100;
    const backdrop = this.popup.querySelector('[data-popup-backdrop]');
    if (backdrop) {
      backdrop.style.backgroundColor = `rgba(0, 0, 0, ${backdropOpacity})`;
    }
  }
  
  /**
   * Check if popup should be shown based on frequency capping
   */
  shouldShowPopup(frequency) {
    const storageKey = 'maintenance_popup_shown';
    
    try {
      const lastShown = localStorage.getItem(storageKey);
      
      if (!lastShown) return true;
      
      const lastShownTime = parseInt(lastShown, 10);
      const now = Date.now();
      
      if (frequency === 'session') {
        // Session-based: check sessionStorage
        const sessionShown = sessionStorage.getItem(storageKey);
        return !sessionShown;
      } else if (frequency === 'hours') {
        // Time-based: check localStorage with hours
        const frequencyHours = parseInt(this.popup.dataset.frequencyHours || '24', 10);
        const hoursDiff = (now - lastShownTime) / (1000 * 60 * 60);
        return hoursDiff >= frequencyHours;
      }
      
      return true;
    } catch (e) {
      // If localStorage is not available, always show
      return true;
    }
  }
  
  /**
   * Mark popup as shown for frequency capping
   */
  markPopupShown(dismissBehavior) {
    const storageKey = 'maintenance_popup_shown';
    const now = Date.now().toString();
    
    try {
      if (dismissBehavior === 'session') {
        sessionStorage.setItem(storageKey, now);
      } else if (dismissBehavior === 'persistent') {
        const dismissHours = parseInt(this.popup.dataset.dismissHours || '24', 10);
        localStorage.setItem(storageKey, now);
        localStorage.setItem(`${storageKey}_expires`, (Date.now() + dismissHours * 60 * 60 * 1000).toString());
      }
    } catch (e) {
      // Ignore storage errors
      console.warn('Unable to save popup state:', e);
    }
  }
  
  /**
   * Show popup
   */
  showPopup() {
    if (!this.popup) return;
    
    // Store last focused element
    this.lastFocusedElement = document.activeElement;
    
    // Show popup
    this.popup.setAttribute('aria-hidden', 'false');
    
    // Trigger animation
    setTimeout(() => {
      this.popup.classList.add('is-visible');
    }, 10);
    
    // Setup focus trap
    this.setupFocusTrap();
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Fire analytics event
    this.fireAnalyticsEvent('popup_viewed', {
      style: this.popup.dataset.style
    });
  }
  
  /**
   * Hide popup
   */
  hidePopup() {
    if (!this.popup) return;
    
    const dismissBehavior = this.popup.dataset.dismiss;
    
    // Mark as shown
    this.markPopupShown(dismissBehavior);
    
    // Hide popup
    this.popup.classList.remove('is-visible');
    
    setTimeout(() => {
      this.popup.setAttribute('aria-hidden', 'true');
      
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Restore focus
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }
    }, 300);
    
    // Fire analytics event
    this.fireAnalyticsEvent('popup_dismissed', {
      style: this.popup.dataset.style
    });
  }
  
  /**
   * Setup popup close handlers
   */
  initPopupCloseHandlers() {
    const closeButton = this.popup.querySelector('[data-popup-close]');
    const backdrop = this.popup.querySelector('[data-popup-backdrop]');
    
    // Close button
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hidePopup());
    }
    
    // Backdrop click
    if (backdrop) {
      backdrop.addEventListener('click', () => this.hidePopup());
    }
    
    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.getAttribute('aria-hidden') === 'false') {
        this.hidePopup();
      }
    });
  }
  
  /**
   * Setup focus trap for accessibility
   */
  setupFocusTrap() {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    
    this.focusableElements = Array.from(
      this.popup.querySelectorAll(focusableSelectors.join(','))
    );
    
    if (this.focusableElements.length === 0) return;
    
    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];
    
    // Focus first element
    firstElement.focus();
    
    // Trap focus
    this.popup.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }
  
  /**
   * Initialize exit-intent trigger
   */
  initExitIntent() {
    let hasShown = false;
    
    document.addEventListener('mouseout', (e) => {
      if (hasShown) return;
      
      // Check if mouse is leaving viewport from top
      if (e.clientY <= 0 || e.relatedTarget === null) {
        // Only on desktop
        if (window.innerWidth >= 768) {
          hasShown = true;
          this.showPopup();
        }
      }
    });
  }
  
  /**
   * Initialize countdown timers
   */
  initCountdowns() {
    this.countdowns.forEach(countdown => {
      const endDate = countdown.dataset.endDate;
      const timezone = countdown.dataset.timezone || 'UTC';
      
      if (!endDate) return;
      
      // Parse end date
      const endTime = this.parseDateTime(endDate, timezone);
      
      // Update countdown every second
      const updateCountdown = () => {
        const now = Date.now();
        const remaining = endTime - now;
        
        if (remaining <= 0) {
          this.handleCountdownComplete(countdown);
          return;
        }
        
        const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
        const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
        
        this.updateCountdownDisplay(countdown, days, hours, minutes, seconds);
      };
      
      // Initial update
      updateCountdown();
      
      // Update every second
      const interval = setInterval(updateCountdown, 1000);
      
      // Store interval for cleanup
      countdown.dataset.intervalId = interval;
    });
  }
  
  /**
   * Parse date/time string with timezone
   */
  parseDateTime(dateString, timezone) {
    try {
      // Parse as ISO format or basic format
      const date = new Date(dateString);
      return date.getTime();
    } catch (e) {
      console.error('Error parsing countdown date:', e);
      return Date.now() + (24 * 60 * 60 * 1000); // Default to 24 hours from now
    }
  }
  
  /**
   * Update countdown display
   */
  updateCountdownDisplay(countdown, days, hours, minutes, seconds) {
    const daysEl = countdown.querySelector('[data-days]');
    const hoursEl = countdown.querySelector('[data-hours]');
    const minutesEl = countdown.querySelector('[data-minutes]');
    const secondsEl = countdown.querySelector('[data-seconds]');
    
    if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
  }
  
  /**
   * Handle countdown completion
   */
  handleCountdownComplete(countdown) {
    // Clear interval
    const intervalId = countdown.dataset.intervalId;
    if (intervalId) {
      clearInterval(parseInt(intervalId, 10));
    }
    
    // Update display to zeros
    this.updateCountdownDisplay(countdown, 0, 0, 0, 0);
    
    // Fire analytics event
    this.fireAnalyticsEvent('countdown_complete');
    
    // Could auto-disable maintenance modes here if desired
    // For now, just log completion
    console.log('Countdown complete');
  }
  
  /**
   * Fire analytics event (lightweight, no external dependencies)
   */
  fireAnalyticsEvent(eventName, data = {}) {
    // Dispatch custom event for any analytics tools to listen to
    const event = new CustomEvent('maintenance_mode_event', {
      detail: {
        event: eventName,
        data: data,
        timestamp: Date.now()
      }
    });
    
    document.dispatchEvent(event);
    
    // Log to console in development
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      console.log('[Maintenance Mode]', eventName, data);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MaintenanceMode();
  });
} else {
  new MaintenanceMode();
}

// Add animation styles dynamically
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideOutDown {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(20px);
    }
  }
`;
document.head.appendChild(style);

